/**
 * Card profesional de estado de ruta para visualización en TV
 */

import React from 'react';
import { Card, Typography, Box, Stack, Chip, Divider } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RateReviewIcon from '@mui/icons-material/RateReview';
import OrderStates from '../../../../utils/OrderStates';
import { RUTAS_ESPECIALES } from '../../constants';

const CardEstadoRuta = ({ delivery, compact = false }) => {
    const nombreRuta = (delivery.nombreRuta ?? '').toLowerCase();
    const esRutaEspecial = RUTAS_ESPECIALES.some(ruta => nombreRuta.includes(ruta));

    // Configuración de estados con colores e iconos (orden: sin preparar, en preparación, en revisión, preparado)
    const estadosConfig = [
        {
            label: OrderStates.states[0],
            value: delivery.sinPreparar?.total ?? 0,
            desglose: delivery.sinPreparar?.porRuta ?? {},
            color: '#dc2626',
            bgColor: '#fee2e2',
            icon: <PendingActionsIcon sx={{ fontSize: 28 }} />
        },
        {
            label: OrderStates.states[1],
            value: delivery.enPreparacion?.total ?? 0,
            desglose: delivery.enPreparacion?.porRuta ?? {},
            color: '#2563eb',
            bgColor: '#dbeafe',
            icon: <PendingActionsIcon sx={{ fontSize: 28 }} />
        },
        {
            label: OrderStates.states[3],
            value: delivery.enRevision?.total ?? 0,
            desglose: delivery.enRevision?.porRuta ?? {},
            color: '#ea580c',
            bgColor: '#ffedd5',
            icon: <RateReviewIcon sx={{ fontSize: 28 }} />
        },
        {
            label: OrderStates.states[2],
            value: delivery.preparado?.total ?? 0,
            desglose: delivery.preparado?.porRuta ?? {},
            color: '#16a34a',
            bgColor: '#dcfce7',
            icon: <CheckCircleIcon sx={{ fontSize: 28 }} />
        }
    ];

    return (
        <Card
            elevation={0}
            sx={{
                background: '#ffffff',
                borderRadius: 3,
                border: '2px solid #e9ecef',
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
        >
            {/* Header corporativo */}
            <Box
                sx={{
                    background: '#cc071e',
                    px: 3,
                    py: compact ? 2 : 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2.5
                }}
            >
                <LocalShippingIcon sx={{ fontSize: compact ? 44 : 52, color: 'white' }} />
                <Typography
                    variant="h4"
                    sx={{
                        color: 'white',
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        letterSpacing: 1.5,
                        flex: 1,
                        fontSize: compact ? '1.8rem' : '2.3rem',
                        lineHeight: 1.2
                    }}
                >
                    {delivery.nombreRuta.toUpperCase()}
                </Typography>
                <Box
                    sx={{
                        width: compact ? 70 : 85,
                        height: compact ? 70 : 85,
                        borderRadius: '50%',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        border: '3px solid rgba(255,255,255,0.3)',
                        flexShrink: 0
                    }}
                >
                    <Typography
                        sx={{
                            color: '#cc071e',
                            fontWeight: 800,
                            fontSize: compact ? '2.2rem' : '2.7rem',
                            lineHeight: 1
                        }}
                    >
                        {delivery.total}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ px: compact ? 3 : 3.5, pt: compact ? 1.5 : 2, pb: compact ? 2 : 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Contadores con badges visuales */}
                <Box sx={{ flex: 1 }}>
                    <Stack spacing={compact ? 0.75 : 1} sx={{ mb: 0 }}>
                        {estadosConfig.map((estado, index) => {
                            const hasDesglose = esRutaEspecial && Object.keys(estado.desglose).length > 0;

                            return (
                                <Box key={index}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: compact ? 2 : 2.5,
                                            py: compact ? 0.75 : 1
                                        }}
                                    >
                                        {/* Icono en círculo */}
                                        <Box
                                            sx={{
                                                width: compact ? 50 : 58,
                                                height: compact ? 50 : 58,
                                                borderRadius: '50%',
                                                background: estado.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                boxShadow: `0 4px 10px ${estado.color}40`
                                            }}
                                        >
                                            {React.cloneElement(estado.icon, {
                                                sx: { fontSize: compact ? 28 : 32, color: 'white' }
                                            })}
                                        </Box>

                                        {/* Label */}
                                        <Typography
                                            sx={{
                                                flex: 1,
                                                fontSize: compact ? '1.25rem' : '1.4rem',
                                                fontWeight: 700,
                                                color: '#495057',
                                                lineHeight: 1.2,
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.5
                                            }}
                                        >
                                            {estado.label}
                                        </Typography>

                                        {/* Valor */}
                                        <Box
                                            sx={{
                                                minWidth: compact ? 70 : 80,
                                                height: compact ? 54 : 62,
                                                borderRadius: 2,
                                                background: estado.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: `0 3px 8px ${estado.color}40`
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: 'white',
                                                    fontWeight: 800,
                                                    fontSize: compact ? '1.7rem' : '2rem'
                                                }}
                                            >
                                                {estado.value}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Desglose por sub-ruta debajo de cada estado */}
                                    {hasDesglose && (
                                        <Box
                                            sx={{
                                                ml: compact ? 5.5 : 6.5,
                                                mb: compact ? 0.5 : 0.75,
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: compact ? 0.75 : 1,
                                                maxHeight: compact ? 76 : 84, // Altura para exactamente 2 filas
                                                overflow: 'hidden',
                                                alignContent: 'flex-start'
                                            }}
                                        >
                                            {Object.entries(estado.desglose).map(([key, value]) => (
                                                <Chip
                                                    key={key}
                                                    label={`${key.toUpperCase()}: ${value}`}
                                                    size="medium"
                                                    sx={{
                                                        backgroundColor: estado.bgColor,
                                                        borderColor: estado.color,
                                                        border: `2px solid ${estado.color}`,
                                                        color: estado.color,
                                                        fontWeight: 800,
                                                        fontSize: compact ? '0.95rem' : '1.05rem',
                                                        height: compact ? 32 : 36,
                                                        '& .MuiChip-label': {
                                                            px: 2,
                                                            letterSpacing: 0.5
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </Box>
        </Card>
    );
};

export default CardEstadoRuta;
